import type { FC } from 'react'
import type { LucideIcon } from 'lucide-react'
import type { SceneProps } from '@/components/landing/games/scene-ui'
import type { CardValue } from '@/components/landing/games/playing-card'

import { useRef, useState } from 'react'
import { ChevronDown, ChevronUp } from 'lucide-react'
import { cn } from '@/lib/utils'
import { useSequence } from '@/hooks/use-sequence'
import PlayingCard, { DealtCard, drawCard } from '@/components/landing/games/playing-card'
import { SceneBadge, SceneStat } from '@/components/landing/games/scene-ui'

type Call = 'higher' | 'lower'
type Phase = 'calling' | 'right' | 'wrong' | 'cashed'

type PastCard = {
    id: number
    card: CardValue
}

const higherChance = (rank: number): number => (13 - rank) / 13
const lowerChance = (rank: number): number => (rank - 1) / 13
const chooseCall = (rank: number): Call => (rank <= 7 ? 'higher' : 'lower')

const POSTER_CARD: CardValue = { rank: 9, suit: 'spades' }

const POSTER_PAST: PastCard[] = [
    { id: 2, card: { rank: 4, suit: 'hearts' } },
    { id: 1, card: { rank: 12, suit: 'clubs' } }
]

interface CallRowProps {
    label: string
    chance: number
    active: boolean
    icon: LucideIcon
}

const CallRow: FC<CallRowProps> = ({ label, chance, active, icon: Icon }) => {
    return (
        <div
            className={cn(
                'flex items-center justify-between rounded-xl border px-3 py-2 transition-all duration-300',
                active ? 'border-ember/60 bg-ember/10 text-bone' : 'border-white/[0.06] bg-white/[0.02] text-steel'
            )}
        >
            <span className='flex items-center gap-2 text-sm'>
                <Icon aria-hidden='true' className={cn('size-4', active && 'text-ember')} />
                {label}
            </span>
            <span className='font-mono text-[11px] tabular-nums'>
                {(chance * 100).toFixed(1)}%
            </span>
        </div>
    )
}

const HiloScene: FC<SceneProps> = ({ active }) => {
    const [current, setCurrent] = useState<CardValue>(POSTER_CARD)
    const [next, setNext] = useState<CardValue | null>(null)
    const [nextKey, setNextKey] = useState(0)
    const [call, setCall] = useState<Call | null>('lower')
    const [multiplier, setMultiplier] = useState(2.31)
    const [past, setPast] = useState<PastCard[]>(POSTER_PAST)
    const [phase, setPhase] = useState<Phase>('calling')
    const pastId = useRef(3)

    useSequence(active, async ({ wait }) => {
        let card = drawCard()
        let chain = 1

        setCurrent(card)
        setNext(null)
        setCall(null)
        setMultiplier(1)
        setPast([])
        setPhase('calling')

        if (!(await wait(900))) return

        for (let step = 0; step < 4; step++) {
            const choice = chooseCall(card.rank)
            setCall(choice)

            if (!(await wait(650))) return

            const drawn = drawCard()
            setNext(drawn)
            setNextKey(previous => previous + 1)

            if (!(await wait(800))) return

            const right = choice === 'higher' ? drawn.rank > card.rank : drawn.rank < card.rank

            if (!right) {
                setPhase('wrong')
                await wait(1700)
                return
            }

            chain *= 0.99 / (choice === 'higher' ? higherChance(card.rank) : lowerChance(card.rank))
            setMultiplier(chain)
            setPhase('right')

            if (!(await wait(600))) return

            const id = pastId.current
            const previousCard = card
            pastId.current += 1
            setPast(previous => [{ id, card: previousCard }, ...previous].slice(0, 4))

            card = drawn
            setCurrent(drawn)
            setNext(null)
            setCall(null)
            setPhase('calling')

            if (!(await wait(350))) return
        }

        setPhase('cashed')
        await wait(1700)
    })

    return (
        <div className='absolute inset-0 flex flex-col justify-center gap-4 px-5 pb-5 pt-14 @md:flex-row @md:items-center @md:gap-6 @md:px-6'>
            <div className='flex items-center justify-center gap-3'>
                <PlayingCard card={current} faceUp size='lg' />

                {next
                    ? <DealtCard key={nextKey} card={next} size='lg' highlight={phase === 'wrong' ? 'dim' : phase === 'right' ? 'win' : null} />
                    : <PlayingCard card={null} faceUp={false} size='lg' className='opacity-50' />}
            </div>

            <div className='flex min-w-0 flex-1 flex-col gap-2'>
                <CallRow label='Higher' chance={higherChance(current.rank)} active={call === 'higher'} icon={ChevronUp} />
                <CallRow label='Lower' chance={lowerChance(current.rank)} active={call === 'lower'} icon={ChevronDown} />

                <div className='mt-1 flex items-end justify-between gap-3'>
                    <SceneStat label='Multiplier' value={`×${multiplier.toFixed(2)}`} tone='ember' />

                    <div className='flex items-center gap-1.5'>
                        <div className='hidden gap-1 @lg:flex'>
                            {past.map(item => (
                                <PlayingCard key={item.id} card={item.card} faceUp size='sm' className='animate-in fade-in slide-in-from-left-2 duration-300' />
                            ))}
                        </div>

                        {phase === 'calling' && <SceneBadge tone='muted'>Calling</SceneBadge>}
                        {phase === 'right' && <SceneBadge tone='ember'>Right call</SceneBadge>}
                        {phase === 'wrong' && <SceneBadge tone='danger'>Wrong call</SceneBadge>}
                        {phase === 'cashed' && <SceneBadge tone='ember'>Cashed out</SceneBadge>}
                    </div>
                </div>
            </div>
        </div>
    )
}

export default HiloScene
